import { useState, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { useNavigate, useLocation } from 'react-router-dom'
import api from '../util/api'

const VerifyCode = () => {
    const [email, setEmail] = useState('')
    const [code, setCode] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [canResend, setCanResend] = useState(false)
    const [resendTimer, setResendTimer] = useState(60)
    const [cookies, setCookie] = useCookies(['token'])
    const navigate = useNavigate()
    const location = useLocation()

    // Get email from navigation state
    useEffect(() => {
        if (location.state?.email) {
            setEmail(location.state.email)
        } else {
            // No email in state - redirect back to login
            navigate('/login')
        }
    }, [location, navigate])

    // Countdown timer for resend button
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
            return () => clearTimeout(timer)
        } else {
            setCanResend(true)
        }
    }, [resendTimer])

    const handleVerifyCode = async (e) => {
        e.preventDefault()

        if (!loading) {
            setLoading(true)
            setError('')

            try {
                const resp = await api().postCode({email: email, code: code})

                if (resp.status === 200) {
                    // Store auth token
                    setCookie('token', resp.data.authToken, { path: '/' })

                    // Check if new user or existing user
                    if (resp.data.isNewUser || !resp.data.user.firstName || !resp.data.user.lastName) {
                        // New user - redirect to onboarding to collect name/username
                        navigate('/onboarding')
                    } else if (resp.data.user.onboardingComplete === false ||
                               !resp.data.user.birthMonth ||
                               !resp.data.user.birthDay) {
                        // Existing user but onboarding incomplete
                        navigate('/onboarding')
                    } else {
                        // Existing user, fully set up
                        navigate('/home')
                    }
                }
            } catch (error) {
                if (error.response?.status === 400) {
                    setError('Invalid or expired code. Please try again.')
                } else {
                    setError('Failed to verify code. Please try again.')
                }
            }

            setLoading(false)
        }
    }

    const handleResendCode = async () => {
        if (canResend && !loading) {
            setLoading(true)
            setError('')

            try {
                const resp = await api().getCode({email})

                if (resp.status === 200) {
                    // Reset timer
                    setResendTimer(60)
                    setCanResend(false)
                    setError('')
                }
            } catch (error) {
                setError('Failed to resend code. Please try again.')
            }

            setLoading(false)
        }
    }

    return (
        <div className="bg-gradient-to-b from-blue-400 to-green-700 min-h-screen flex items-center justify-center">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-sm w-full">
                <h2 className="text-2xl font-semibold mb-2 text-center">
                    Enter verification code
                </h2>
                <p className="text-gray-600 mb-6 text-center text-sm">
                    We sent a code to {email}
                </p>

                {error && <p className="text-red-500 text-center mb-4">{error}</p>}

                <form onSubmit={handleVerifyCode}>
                    <div className="mb-6">
                        <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                            Verification Code
                        </label>
                        <input
                            type="text"
                            id="code"
                            className="w-full p-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
                            placeholder="000000"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                            maxLength={6}
                            required
                        />
                    </div>

                    <button
                        disabled={loading || code.length !== 6}
                        type="submit"
                        className={
                            loading || code.length !== 6
                                ? "w-full p-2 bg-gray-400 text-white rounded-md cursor-not-allowed"
                                : "w-full p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
                        }
                    >
                        {loading ? 'Verifying...' : 'Verify code'}
                    </button>
                </form>

                {/* Resend Code Button */}
                <div className="text-center mt-4">
                    {canResend ? (
                        <button
                            onClick={handleResendCode}
                            disabled={loading}
                            className="text-sm text-blue-500 hover:text-blue-700 hover:underline cursor-pointer disabled:opacity-50"
                        >
                            Resend code
                        </button>
                    ) : (
                        <p className="text-sm text-gray-500">
                            Resend code in {resendTimer}s
                        </p>
                    )}
                </div>

                {/* Back to Login */}
                <div className="text-center mt-3">
                    <button
                        onClick={() => navigate('/login')}
                        className="text-sm text-gray-500 hover:underline"
                        type="button"
                    >
                        Back to login
                    </button>
                </div>
            </div>
        </div>
    )
}

export default VerifyCode
